import ModuleIcon from './ModuleIcon';
import WorkspaceNavigation from './WorkspaceNavigation';
import ErrorBoundary from './ErrorBoundary';
import React, { useState, useMemo, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import BriefyAssistant from './BriefyAssistant';
import ProfileImage from './ProfileImage';
import TrialBanner from './TrialBanner';
import InstallBanner from './InstallBanner';
import BriefyIdle from './BriefyIdle';
import NotificationBell from './layout/NotificationBell';
import {
  LayoutDashboard, ShoppingCart, Settings, LogOut, Menu, X, CreditCard, FileText,
  CheckSquare, Crown, Shield, Users, Truck, UserCircle, Download,
  ChevronDown, ChevronRight, MessageCircle, BarChart3, Building, Flame, PiggyBank, Newspaper,
  TrendingUp, Target, PieChart, LineChart, DollarSign, CalendarDays, Car, Building2,
  Contact2, Globe, StickyNote, Scale, ChevronsLeft, ChevronsRight, Search, FolderArchive,
} from 'lucide-react';

const BriefcaseLogo = ({ size = 32 }) => (
  <img src="/briefcase-logo.png" alt="BRIEFCASE" width={size} height={size} style={{ objectFit: 'contain' }} />
);

const NAV_TINTS = {
  blue: 'bg-blue-500/25 text-blue-300',
  sky: 'bg-sky-500/20 text-sky-300',
  cyan: 'bg-cyan-500/20 text-cyan-300',
  teal: 'bg-teal-500/20 text-teal-300',
  emerald: 'bg-emerald-500/20 text-emerald-300',
  green: 'bg-green-500/20 text-green-300',
  amber: 'bg-amber-500/20 text-amber-300',
  yellow: 'bg-yellow-500/20 text-yellow-300',
  orange: 'bg-orange-500/20 text-orange-300',
  red: 'bg-red-500/20 text-red-300',
  rose: 'bg-rose-500/20 text-rose-300',
  pink: 'bg-pink-500/20 text-pink-300',
  purple: 'bg-purple-500/20 text-purple-300',
  violet: 'bg-violet-500/20 text-violet-300',
  indigo: 'bg-indigo-500/25 text-indigo-300',
  slate: 'bg-slate-500/25 text-slate-300',
};

export default function Layout({ children }) {
  useEffect(() => {
    document.body.classList.add('bc-app-active');
    return () => document.body.classList.remove('bc-app-active');
  }, []);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem('bc_sidebar_collapsed') === '1');
  const [showLanguageMenu, setShowLanguageMenu] = useState(false);
  const [expandedMenu, setExpandedMenu] = useState(null);
  const [navSearch, setNavSearch] = useState('');
  const { user, logout } = useAuth();
  const { t, language, setLanguage, supportedLanguages } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  const isAdmin = user?.role === 'admin' || user?.email?.toLowerCase() === 'realtorluisfmontoya@gmail.com';

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const toggleCollapsed = () => {
    const next = !collapsed;
    setCollapsed(next);
    localStorage.setItem('bc_sidebar_collapsed', next ? '1' : '0');
  };

  const navGroups = [
    {
      id: 'main',
      label: null,
      items: [
        { path: '/dashboard', icon: LayoutDashboard, label: t('nav.dashboard'), tint: 'blue' },
        { path: '/pos', icon: ShoppingCart, label: t('nav.posUnified', 'Caja / POS'), tint: 'emerald' },
        {
          id: 'finanzas',
          icon: TrendingUp,
          tint: 'green',
          label: t('nav.finances', 'Finanzas'),
          subItems: [
            { path: '/finanzas', icon: TrendingUp, label: t('nav.financesDashboard', 'Dashboard Financiero'), iconColor: 'text-green-300' },
            { path: '/finanzas/flujo-caja', icon: DollarSign, label: t('nav.cashFlow', 'Flujo de Caja'), iconColor: 'text-emerald-300' },
            { path: '/finanzas/metas', icon: Target, label: t('nav.salesGoals', 'Metas de Ventas'), iconColor: 'text-amber-300' },
            { path: '/finanzas/gastos', icon: PieChart, label: t('nav.expenseAnalysis', 'Análisis de Gastos'), iconColor: 'text-rose-300' },
            { path: '/finanzas/comparativo', icon: BarChart3, label: t('nav.comparative', 'Comparativo'), iconColor: 'text-sky-300' },
            { path: '/finanzas/rentabilidad', icon: LineChart, label: t('nav.profitability', 'Rentabilidad'), iconColor: 'text-teal-300' },
            { path: '/finanzas/proyecciones', icon: TrendingUp, label: t('nav.projections', 'Proyecciones'), iconColor: 'text-violet-300' },
            { path: '/finanzas/analisis', icon: BarChart3, label: t('nav.analysis', 'Análisis Avanzado'), iconColor: 'text-cyan-300' },
          ],
        },
        { path: '/budget', icon: PiggyBank, label: t('nav.budget', 'Presupuestos'), tint: 'amber' },
      ],
    },
    {
      id: 'crm',
      label: t('nav.groupCrm', 'CRM y Clientes'),
      items: [
        { path: '/leads', icon: Flame, label: t('nav.leads', 'Leads CRM'), tint: 'orange' },
        { path: '/customers', icon: UserCircle, label: t('nav.clients', 'Clientes'), tint: 'sky' },
        { path: '/contacts', icon: Contact2, label: t('nav.contacts', 'Contactos'), tint: 'cyan' },
        { path: '/appointments', icon: CalendarDays, label: t('nav.appointments', 'Citas'), tint: 'violet' },
        { path: '/chat', icon: MessageCircle, label: t('nav.chat', 'Comunicación'), tint: 'pink' },
        { path: '/realestate', icon: Building, label: t('pm.workspaceTitle', 'Propiedades y Gestión'), tint: 'teal' },
        { path: '/companies', icon: Building2, label: t('nav.companies', 'Empresas'), tint: 'indigo' },
      ],
    },
    {
      id: 'ops',
      label: t('nav.groupOps', 'Operación'),
      items: [
        { path: '/services#archivador', icon: FolderArchive, label: t('nav.archive', 'Archivador'), tint: 'violet' },
        { path: '/facturas', icon: FolderArchive, label: t('nav.invoices', 'Facturas'), tint: 'yellow' },
        { path: '/bills', icon: FileText, label: t('nav.bills', 'Pagos'), tint: 'rose' },
        { path: '/tasks', icon: CheckSquare, label: t('nav.tasks', 'Tareas'), tint: 'green' },
        { path: '/notes', icon: StickyNote, label: t('nav.notes', 'Mis Notas'), tint: 'amber' },
        { path: '/payroll', icon: Users, label: t('nav.payroll', 'Nómina'), tint: 'cyan' },
        { path: '/purchases', icon: Truck, label: t('nav.purchases', 'Compras'), tint: 'orange' },
        { path: '/transport', icon: Car, label: t('nav.transport', 'Transporte'), tint: 'sky' },
        { path: '/price-index', icon: Scale, label: t('nav.priceIndex', 'Price Index'), tint: 'purple' },
      ],
    },
    {
      id: 'more',
      label: t('nav.groupMore', 'Más'),
      items: [
        { path: '/news', icon: Newspaper, label: t('nav.news', 'Noticias'), highlight: true, tint: 'red' },
        { path: '/business-card', icon: CreditCard, label: t('nav.businessCard', 'Tarjeta'), tint: 'indigo' },
        { path: '/pricing', icon: Crown, label: t('nav.pricing', 'Planes'), highlight: !isAdmin, tint: 'yellow' },
        { path: '/instalar', icon: Download, label: t('nav.installApp', 'Instalar App'), highlight: !isAdmin, tint: 'emerald' },
      ],
    },
    {
      id: 'admin',
      label: t('nav.groupAdmin', 'Administración'),
      adminOnly: true,
      items: [
        { path: '/admin', icon: Shield, label: t('nav.admin', 'Admin'), admin: true, tint: 'red' },
        { path: '/admin/revenue', icon: DollarSign, label: t('nav.revenue', 'Revenue'), admin: true, tint: 'green' },
      ],
    },
  ];

  const bottomNav = [
    { path: '/dashboard', icon: LayoutDashboard, label: t('nav.dashboard', 'Inicio'), on: 'text-[#2563EB]', bar: 'bg-[#2563EB]' },
    { path: '/pos', icon: ShoppingCart, label: 'POS', on: 'text-emerald-600', bar: 'bg-emerald-500' },
    { path: '/customers', icon: UserCircle, label: 'CRM', on: 'text-orange-600', bar: 'bg-orange-500' },
    { path: '/finanzas', icon: TrendingUp, label: t('nav.finances', 'Finanzas'), on: 'text-teal-600', bar: 'bg-teal-500' },
  ];

  const toggleSubmenu = (menuId) => setExpandedMenu(expandedMenu === menuId ? null : menuId);

  // Flat search results across all items + subitems
  const searchResults = useMemo(() => {
    const norm = (s) => (s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    const q = norm(navSearch.trim());
    if (!q) return null;
    const out = [];
    navGroups.filter((g) => !g.adminOnly || isAdmin).forEach((g) => {
      g.items.filter((it) => !it.admin || isAdmin).forEach((it) => {
        if (it.subItems) {
          it.subItems.forEach((s) => {
            if (norm(s.label).includes(q)) out.push(s);
          });
        } else if (norm(it.label).includes(q)) {
          out.push(it);
        }
      });
    });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navSearch, isAdmin, language]);

  const itemClasses = (isActive) => `
    group relative flex items-center ${collapsed ? 'justify-center px-0 py-2' : 'px-2.5 py-2'} gap-3 rounded-xl transition-colors duration-200 touch-manipulation w-full
    ${isActive ? 'bg-[#2563EB] text-white shadow-lg shadow-blue-900/40' : 'text-slate-300 hover:bg-white/10 hover:text-white'}
  `;

  const IconChip = ({ icon: Icon, module, active, tint = 'blue' }) => (
    <span className={`w-8 h-8 rounded-[10px] flex items-center justify-center shrink-0 transition-colors ${active ? 'bg-white/20 text-white' : NAV_TINTS[tint] || NAV_TINTS.blue}`}>
      <ModuleIcon module={module} tone={tint} size={29} />
    </span>
  );

  const renderItem = (item) => {
    const isActive = location.pathname === item.path;
    return (
      <NavLink
        key={item.path}
        to={item.path}
        onClick={() => { setSidebarOpen(false); setNavSearch(''); }}
        className={({ isActive: a }) => itemClasses(a)}
        data-testid={`nav-link-${item.path.replace(/\//g, '-').replace(/^-/, '')}`}
        title={collapsed ? item.label : undefined}
        style={{ WebkitTapHighlightColor: 'transparent' }}
      >
        <IconChip module={item.path || item.id} icon={item.icon} active={isActive} tint={item.tint} />
        {!collapsed && <span className={`text-sm ${isActive ? 'font-semibold' : 'font-medium'}`}>{item.label}</span>}
        {!collapsed && item.highlight && (
          <span className="ml-auto text-[10px] bg-gradient-to-r from-[#F5D77A] to-[#D4AF37] text-[#0B132B] px-2 py-0.5 rounded-full font-bold">PRO</span>
        )}
        {!collapsed && item.admin && <span className="ml-auto text-xs text-[#F5D77A]">★</span>}
        {!collapsed && !item.highlight && !item.admin && (
          <ChevronRight className={`w-4 h-4 ml-auto shrink-0 ${isActive ? 'text-white/70' : 'text-slate-600 group-hover:text-slate-400'}`} />
        )}
      </NavLink>
    );
  };

  const sidebarWidth = collapsed ? 'w-[76px]' : 'w-72';
  const mainMargin = collapsed ? 'lg:ml-[76px]' : 'lg:ml-72';
  const headerLeft = collapsed ? 'lg:left-[76px]' : 'lg:left-72';
  const userInitial = (user?.business_name?.charAt(0) || user?.email?.charAt(0) || 'U').toUpperCase();

  return (
    <div className="min-h-screen bg-[#F8FAFC] relative">

      {/* ===== Top Header (all breakpoints) ===== */}
      <header className={`fixed top-0 right-0 left-0 ${headerLeft} h-14 z-40 flex items-center justify-between px-3 lg:px-6 transition-all duration-300 border-b border-white/10`} style={{ background: 'linear-gradient(90deg, #0B1533 0%, #0E1D45 100%)' }} data-testid="top-header">
        <div className="flex items-center gap-2 min-w-0">
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden p-2 text-slate-200 hover:text-white transition-colors rounded-lg hover:bg-white/10"
            data-testid="mobile-menu-btn"
          >
            <Menu className="w-6 h-6" />
          </button>
          <div className="flex items-center gap-2">
            <BriefcaseLogo size={26} />
            <span className="text-white tracking-wide text-base" style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 800 }}>BRIEFCASE</span>
          </div>
          <p className="hidden lg:block text-sm text-slate-300 truncate ml-4 pl-4 border-l border-white/15">
            {t('layout.hello', 'Hola')}, <span className="font-bold text-white">{user?.business_name || user?.email}</span> 👋
          </p>
        </div>

        <div className="flex items-center gap-1.5 lg:gap-2">
          {/* Language */}
          <div className="relative">
            <button
              onClick={() => setShowLanguageMenu(!showLanguageMenu)}
              className="p-2 text-slate-300 hover:text-white transition-colors flex items-center gap-1 rounded-lg hover:bg-white/10"
              data-testid="language-toggle-mobile"
            >
              <Globe className="w-5 h-5" />
              <span className="text-xs uppercase font-bold">{language}</span>
            </button>
            {showLanguageMenu && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setShowLanguageMenu(false)} />
                <div className="absolute right-0 top-full mt-2 bg-white border border-[#E2E8F0] rounded-xl shadow-lg z-50 py-2 min-w-[140px]">
                  {supportedLanguages?.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => { setLanguage(lang.code); setShowLanguageMenu(false); }}
                      className={`w-full px-4 py-2 text-left text-sm flex items-center gap-2 hover:bg-[#F1F5F9] transition-colors ${
                        language === lang.code ? 'text-[#2563EB] font-bold bg-[#EFF6FF]' : 'text-slate-600'
                      }`}
                      data-testid={`language-option-${lang.code}`}
                    >
                      <span>{lang.flag}</span>
                      <span>{lang.name}</span>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Briefy daily digest bell */}
          <NotificationBell />

          {/* Avatar */}
          <button
            onClick={() => navigate('/settings')}
            data-testid="header-avatar-btn"
            className="w-9 h-9 rounded-full bg-[#2563EB] text-white font-bold text-sm flex items-center justify-center overflow-hidden shrink-0 ring-2 ring-white/25 hover:ring-white/60 transition-shadow"
            title={user?.business_name || user?.email}
          >
            <ProfileImage src={user?.profile_photo} fallback={userInitial} />
          </button>
        </div>
      </header>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50" onClick={() => setSidebarOpen(false)} />
      )}

      {/* ===== Sidebar — navy gradient ===== */}
      <aside className={`
        fixed top-0 left-0 h-full ${sidebarWidth} w-72 lg:${sidebarWidth}
        z-50 transform transition-all duration-300 flex flex-col
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:translate-x-0
      `} style={{ background: 'linear-gradient(180deg, #0B1533 0%, #0D1B3E 55%, #10224E 100%)' }}>
        {/* Logo Header */}
        <div className={`${collapsed ? 'px-2 py-4' : 'px-4 py-4'} shrink-0`}>
          <div className={`flex items-center ${collapsed ? 'justify-center' : 'justify-between'}`}>
            <div className="flex items-center gap-2.5">
              <div className="p-1.5 rounded-xl bg-[#2563EB]/20 border border-[#2563EB]/30">
                <BriefcaseLogo size={collapsed ? 26 : 28} />
              </div>
              {!collapsed && (
                <div className="leading-tight">
                  <h1 className="text-white tracking-wide text-base" style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 800 }}>BRIEFCASE</h1>
                  <p className="text-[10px] text-slate-400 font-medium tracking-wider">Business Suite</p>
                </div>
              )}
            </div>
            <button onClick={() => setSidebarOpen(false)} className="lg:hidden p-1 text-slate-400 hover:text-white transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* User card */}
        {!collapsed && (
          <div className="mx-3 mb-3 px-3 py-2.5 rounded-xl bg-white/5 border border-white/10 shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-[#2563EB] flex items-center justify-center overflow-hidden shrink-0">
                <ProfileImage src={user?.profile_photo} fallback={userInitial} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white font-semibold text-sm truncate">{user?.business_name || t('common.myBusiness', 'Mi Negocio')}</p>
                <p className="text-slate-400 text-xs truncate">{user?.email}</p>
              </div>
            </div>
          </div>
        )}

        {/* Module search */}
        {!collapsed && (
          <div className="mx-3 mb-2 relative shrink-0">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <input
              value={navSearch}
              onChange={(e) => setNavSearch(e.target.value)}
              placeholder={t('nav.searchModule', 'Buscar módulo...')}
              data-testid="sidebar-search-input"
              className="w-full bg-white/10 border border-white/10 rounded-xl pl-9 pr-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-[#2563EB]/60"
            />
          </div>
        )}

        {/* Navigation */}
        <nav className={`${collapsed ? 'px-2' : 'px-3'} pb-3 space-y-0.5 overflow-y-auto flex-1 relative`}>
          {searchResults ? (
            searchResults.length === 0 ? (
              <p className="text-slate-500 text-xs text-center py-6">{t('nav.noResults', 'Sin resultados')}</p>
            ) : (
              <div className="space-y-0.5">{searchResults.map((item) => renderItem(item))}</div>
            )
          ) : (
            navGroups.filter((g) => !g.adminOnly || isAdmin).map((group) => (
              <div key={group.id}>
                {group.label && !collapsed && (
                  <p className="px-3 pt-4 pb-1.5 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500 select-none">{group.label}</p>
                )}
                {group.label && collapsed && <div className="my-3 border-t border-white/10" />}
                <div className="space-y-0.5">
                  {group.items.filter((item) => !item.admin || isAdmin).map((item) => (
                    item.subItems ? (
                      <div key={item.id}>
                        <button
                          onClick={() => { if (collapsed) { toggleCollapsed(); } toggleSubmenu(item.id); }}
                          className={itemClasses(item.subItems.some((sub) => location.pathname === sub.path))}
                          data-testid={`nav-group-${item.id}`}
                          title={collapsed ? item.label : undefined}
                        >
                          <IconChip module={item.path || item.id} icon={item.icon} active={item.subItems.some((sub) => location.pathname === sub.path)} tint={item.tint} />
                          {!collapsed && <span className="font-medium text-sm">{item.label}</span>}
                          {!collapsed && <ChevronDown className={`w-4 h-4 ml-auto transition-transform duration-200 text-slate-500 ${expandedMenu === item.id ? 'rotate-180' : ''}`} />}
                        </button>
                        {expandedMenu === item.id && !collapsed && (
                          <div className="mt-1 ml-5 space-y-0.5 border-l border-white/10 pl-2">
                            {item.subItems.map((subItem) => (
                              <NavLink
                                key={subItem.path}
                                to={subItem.path}
                                onClick={() => setSidebarOpen(false)}
                                data-testid={`nav-link-${subItem.path.replace(/\//g, '-').replace(/^-/, '')}`}
                                className={({ isActive }) => `
                                  flex items-center gap-2.5 px-3 py-2 rounded-lg transition-colors duration-200 text-sm
                                  ${isActive ? 'bg-[#2563EB] text-white font-semibold' : 'text-slate-400 hover:bg-white/10 hover:text-white font-medium'}
                                `}
                              >
                                <ModuleIcon module={subItem.path} size={24} className="shrink-0" />
                                <span>{subItem.label}</span>
                              </NavLink>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : renderItem(item)
                  ))}
                </div>
              </div>
            ))
          )}

          {/* ===== BRIEFY block ===== */}
          {!collapsed && (
            <div
              className="mt-4 rounded-2xl p-3 border border-[#2563EB]/50 bg-[#2563EB]/10"
              style={{ boxShadow: '0 0 18px rgba(37,99,235,0.25)' }}
              data-testid="sidebar-briefy-block"
            >
              <div className="flex items-center gap-2.5 mb-2.5">
                <div className="w-9 h-9 rounded-full bg-white p-0.5 overflow-hidden shrink-0" style={{ boxShadow: '0 0 0 2px rgba(37,99,235,0.6)' }}>
                  <img src="/mascot.png" alt="Briefy" className="w-full h-full object-contain" />
                </div>
                <div className="leading-tight">
                  <p className="text-white font-bold text-sm" style={{ fontFamily: "'Montserrat', sans-serif" }}>BRIEFY</p>
                  <p className="text-[10px] text-[#93C5FD]">{t('briefy.aiAssistant', 'Asistente de IA')}</p>
                </div>
              </div>
              <div className="space-y-1.5">
                <button
                  onClick={() => { setSidebarOpen(false); window.dispatchEvent(new Event('briefy:open')); }}
                  data-testid="sidebar-briefy-talk"
                  className="w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-xs font-semibold transition-colors"
                >
                  <MessageCircle className="w-3.5 h-3.5" /> {t('briefy.talk', 'Hablar con BRIEFY')}
                </button>
                <button
                  onClick={() => { setSidebarOpen(false); navigate('/customers'); }}
                  data-testid="sidebar-briefy-create-client"
                  className="w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-white/10 hover:bg-[#2563EB]/60 text-slate-200 text-xs font-semibold transition-colors"
                >
                  <UserCircle className="w-3.5 h-3.5 text-[#93C5FD]" /> {t('briefy.createClient', 'Crear cliente')}
                </button>
                <button
                  onClick={() => { setSidebarOpen(false); navigate('/pos'); }}
                  data-testid="sidebar-briefy-register-sale"
                  className="w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-white/10 hover:bg-[#2563EB]/60 text-slate-200 text-xs font-semibold transition-colors"
                >
                  <ShoppingCart className="w-3.5 h-3.5 text-[#93C5FD]" /> {t('briefy.registerSale', 'Registrar venta')}
                </button>
              </div>
            </div>
          )}

          <div className="my-3 border-t border-white/10" />
          {renderItem({ path: '/settings', icon: Settings, label: t('nav.settings'), tint: 'slate' })}
          <button
            onClick={handleLogout}
            className={`w-full flex items-center ${collapsed ? 'justify-center' : 'px-2.5'} gap-3 py-2 rounded-xl text-rose-300/70 hover:bg-rose-500/10 hover:text-rose-300 transition-colors duration-200`}
            data-testid="logout-button"
            title={collapsed ? t('auth.logout') : undefined}
          >
            <span className={`w-8 h-8 rounded-[10px] flex items-center justify-center shrink-0 ${collapsed ? '' : 'bg-rose-500/10'}`}>
              <LogOut className="w-4 h-4" strokeWidth={1.9} />
            </span>
            {!collapsed && <span className="font-medium text-sm">{t('auth.logout')}</span>}
          </button>
          <div className="h-12" />
        </nav>

        {/* Collapse toggle — desktop only */}
        <button
          onClick={toggleCollapsed}
          data-testid="sidebar-collapse-btn"
          aria-label={collapsed ? t('common.expand', 'Expandir menú') : t('common.collapse', 'Colapsar menú')}
          className="hidden lg:flex absolute bottom-3 right-3 items-center justify-center w-8 h-8 rounded-lg bg-white/5 border border-white/10 text-slate-400 hover:text-white hover:bg-white/10 transition-colors"
        >
          {collapsed ? <ChevronsRight className="w-4 h-4" /> : <ChevronsLeft className="w-4 h-4" />}
        </button>
      </aside>

      {/* Main Content */}
      <main className={`bc-workspace ${mainMargin} min-h-screen transition-all duration-300`}>
        <div className="p-4 lg:p-6 pt-[72px] lg:pt-[76px] pb-36 lg:pb-6">
          <WorkspaceNavigation groups={navGroups.filter(g=>!g.adminOnly||isAdmin).map(g=>({...g,items:g.items.filter(i=>!i.admin||isAdmin)}))} pathname={location.pathname}/>
          <ErrorBoundary key={location.pathname}>{children}</ErrorBoundary>
        </div>
      </main>

      {/* Mobile Bottom Navigation */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 h-16 bg-white/95 backdrop-blur-xl border-t border-[#E2E8F0] z-40 grid grid-cols-5" data-testid="mobile-bottom-nav" style={{ boxShadow: '0 -4px 20px rgba(0,0,0,0.06)' }}>
        {bottomNav.map((item) => {
          const active = location.pathname === item.path;
          return (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              data-testid={`bottom-nav-${item.path.replace(/\//g, '')}`}
              aria-current={active ? "page" : undefined}
              className="relative flex flex-col items-center justify-center gap-0.5 touch-manipulation"
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              {active && <span className={`absolute top-0 left-1/2 -translate-x-1/2 w-8 h-[3px] rounded-b-full ${item.bar}`} />}
              <ModuleIcon module={item.path} size={32} className="bc-bottom-illustration" />
              <span className={`text-[10px] font-bold ${active ? item.on : 'text-slate-400'}`}>{item.label}</span>
            </button>
          );
        })}
        <button
          onClick={() => setSidebarOpen(true)}
          data-testid="bottom-nav-more"
          className="flex flex-col items-center justify-center gap-0.5 touch-manipulation"
          style={{ WebkitTapHighlightColor: 'transparent' }}
        >
          <ModuleIcon module="more" size={32} className="bc-bottom-illustration" />
          <span className="text-[10px] font-semibold text-slate-400">{t('nav.groupMore', 'Más')}</span>
        </button>
      </nav>

      <BriefyAssistant />
      <TrialBanner />
      <InstallBanner />
      <BriefyIdle />
    </div>
  );
}
